import { useEffect, useState, useCallback, useRef } from "react";
import type { Session } from "@supabase/supabase-js";
import { listen } from "@tauri-apps/api/event";
import { invoke } from "@tauri-apps/api/core";
import { supabase, isTauri } from "./supabase";
import Chat from "./ui/Chat";
import Dashboard from "./ui/Dashboard";
import PersonalityTab from "./ui/Personality";
import MediaLibrary from "./ui/MediaLibrary";
import { WalletTopUp } from "./iap";
import { listDrafts, exportData, importData, type LocalDraft } from "./store/drafts";

type View = "dash" | "media" | "settings";

// Private-beta gate (the standard "coming soon" until launch). The 3 internal testers + the App Review
// account. Belt-and-suspenders with the server's ORIORA_CONTENT_ALLOWLIST; essential on the public web URL.
// IAP wallet top-up shows only in the NATIVE iOS app (StoreKit doesn't exist on macOS/web).
const isIOSNative = isTauri && typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
// Mac App Store build (VITE_MAS=1 baked at build time): StoreKit runs natively → same IAP top-up as
// iOS (3.1.1 applies on the Mac App Store too). The Developer-ID DMG build keeps the web-link path.
const isMasMac = isTauri && !isIOSNative && (import.meta as any).env?.VITE_MAS === "1";

const APP_SCHEME = "oriora-social";
const ACCOUNT_URL = "https://orioralabs.com/settings";
const HOW_TO_URL = "https://orioralabs.com/apps/social-content-agent";

// AI-data consent (App Store Guideline 5.1.1(i)/5.1.2): one-time disclosure at first launch after
// sign-in, before any data can be sent to an AI provider. Bump the suffix to re-prompt everyone.
const AI_CONSENT_KEY = "oriora.ai_consent.v1";

const TESTERS = new Set([
  "info@orioralabs.com",
  "technology.innovation26@gmail.com",
  "bogdan.ivas2@gmail.com",
  "appreview@orioralabs.com",
]);

function parseTokens(url: string): { access_token: string; refresh_token: string } | null {
  const frag = url.includes("#") ? url.split("#")[1] : (url.split("?")[1] ?? "");
  const p = new URLSearchParams(frag);
  const access_token = p.get("access_token");
  const refresh_token = p.get("refresh_token");
  return access_token && refresh_token ? { access_token, refresh_token } : null;
}

// iOS sign-in: the Oriora login page inside ASWebAuthenticationSession — the in-app system sheet
// Apple requires (Guideline 4: no external Safari for auth). The oriora-social://auth#tokens
// redirect is captured by the session and applied here.
async function signInIos(): Promise<void> {
  const { callbackUrl } = await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: { url: `https://orioralabs.com/login?return=${APP_SCHEME}`, callbackScheme: APP_SCHEME },
  });
  const toks = parseTokens(callbackUrl);
  if (toks) await supabase.auth.setSession(toks);
}

// iOS account deletion — mirrors signInIos (in-app system sheet, never the external browser).
async function deleteAccountIos(accessToken: string, refreshToken: string): Promise<void> {
  await invoke<{ callbackUrl: string }>("plugin:web-auth|authenticate", {
    payload: {
      url: `https://orioralabs.com/account/delete?return=${APP_SCHEME}#access_token=${accessToken}&refresh_token=${refreshToken}`,
      callbackScheme: APP_SCHEME,
    },
  });
  await supabase.auth.signOut({ scope: "local" });
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  const [view, setView] = useState<View>("dash");
  const [postsKey, setPostsKey] = useState(0);
  const [dashKey, setDashKey] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setReady(true); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    // Native only: tokens arrive via the Tauri deep-link. On web, Supabase's detectSessionInUrl
    // picks up the OAuth redirect fragment automatically — no listener needed.
    let unlisten: Promise<() => void> | null = null;
    if (isTauri) {
      unlisten = listen<string>("auth:deep-link", async (e) => {
        const toks = parseTokens(e.payload);
        if (toks) await supabase.auth.setSession(toks);
      });
    }
    return () => { sub.subscription.unsubscribe(); unlisten?.then((u) => u()); };
  }, []);

  const signOut = useCallback(() => { supabase.auth.signOut(); }, []);

  if (!ready) return <div className="center"><span className="muted">Loading…</span></div>;

  if (!session) return <Login />;

  // Private-beta gate — until launch, only the internal testers get in; everyone else sees "coming soon".
  const email = (session.user?.email ?? "").toLowerCase();
  if (!TESTERS.has(email)) {
    return (
      <div className="center">
        <div className="login-card">
          <div className="brand-lg">Oriora <span className="dot">Content</span></div>
          <h1>Coming soon</h1>
          <p>Oriora Content isn't open to everyone yet. Thanks for your interest — we'll let you know the moment it's ready.</p>
          <button className="btn btn-block" onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  // Guideline 5.1.1(i)/5.1.2: disclose + get consent before any AI data is sent — right after
  // sign-in, before the dashboard (nothing is sent pre-login). Persisted, never shown again.
  if (!consented) {
    return <AiConsent onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* in-session only */ } setConsented(true); }} />;
  }

  return (
    <div className="app">
      <nav className="nav">
        <span className="brand">Oriora <span className="dot">Content</span></span>
        <span style={{ flex: 1 }} />
        <button className={`tab ${view === "dash" ? "on" : ""}`} onClick={() => setView("dash")}>Dashboard</button>
        <button className={`tab ${view === "media" ? "on" : ""}`} onClick={() => setView("media")}>Media</button>
        <button className={`tab ${view === "settings" ? "on" : ""}`} onClick={() => setView("settings")}>Settings</button>
        <AccountMenu email={email} session={session} />
      </nav>
      <main className="content">
        {view === "settings" ? (
          <div className="wrap">
            <PersonalityTab />
            <Posts refreshKey={postsKey} />
          </div>
        ) : view === "media" ? (
          <MediaLibrary />
        ) : (
          <Dashboard refreshKey={dashKey} />
        )}
      </main>

      {/* The steering wheel — the PRIMARY control. A prominent labeled launcher (not a support bubble):
          tell the assistant what content to make. Tap → the chat sheet slides up. */}
      {!chatOpen && (
        <button className="assistant-bar" onClick={() => setChatOpen(true)}>
          <span className="ab-icon">✨</span>
          <span className="ab-text">
            <strong>What should we post?</strong>
            <span className="ab-sub">Tell me a topic or message — “draft a launch teaser”, “schedule it for Friday 9am”, “post it now”.</span>
          </span>
          <span className="ab-go">›</span>
        </button>
      )}
      <aside className={`chat-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="chat-sheet-head">
          <span>✨ Your assistant — tell me what to make</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={() => { setPostsKey((k) => k + 1); setDashKey((k) => k + 1); }} />
      </aside>
    </div>
  );
}

// Sign-in. Native surfaces sign in fully in-app: iOS = ASWebAuthenticationSession (Guideline 4);
// other desktops = the in-app login window. Web = the same identities the Oriora login offers —
// Apple or an email code (login is Apple + email code ONLY; no Google/GitHub identities).
function Login() {
  const [busy, setBusy] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [err, setErr] = useState("");

  const signInNative = async () => {
    setBusy(true);
    try {
      if (isIOSNative) await signInIos();
      else await invoke("open_login_window");
    } catch { /* user dismissed the sheet — let them retry */ }
    finally { setBusy(false); }
  };

  const sendCode = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setBusy(true); setErr("");
    try {
      const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: true } });
      if (error) throw error;
      setCodeSent(true);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const verifyCode = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !code.trim()) return;
    setBusy(true); setErr("");
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code.trim(), type: "email" });
      if (error) throw error;
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const signInApple = () => {
    supabase.auth.signInWithOAuth({ provider: "apple", options: { redirectTo: window.location.origin } }).catch(console.error);
  };

  return (
    <div className="center">
      <div className="login-card">
        <div className="brand-lg">Oriora <span className="dot">Content</span></div>
        <h1>Sign in to get started</h1>
        <p>
          Tell it what to post — it drafts in your voice, you approve, it posts. Your drafts and chats
          live on this device; your queue and preferences in your own GitHub. AI features send only what
          each request needs, and Oriora doesn't retain it.
        </p>
        {isTauri ? (
          <button className="btn-primary btn-block" onClick={signInNative} disabled={busy}>
            {busy ? "Signing in…" : "Sign in with Oriora"}
          </button>
        ) : !codeSent ? (
          <>
            <button className="btn-primary btn-block" onClick={signInApple}> Sign in with Apple</button>
            <div className="muted" style={{ margin: "10px 0 6px", fontSize: 12 }}>or use your email</div>
            <input
              className="field"
              type="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendCode(); }}
              style={{ width: "100%", marginBottom: 8 }}
            />
            <button className="btn btn-block" onClick={sendCode} disabled={busy || !emailInput.trim()}>
              {busy ? "Sending…" : "Email me a sign-in code"}
            </button>
          </>
        ) : (
          <>
            <p className="muted" style={{ fontSize: 12 }}>We emailed a 6-digit code to {emailInput.trim()}.</p>
            <input
              className="field"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") verifyCode(); }}
              style={{ width: "100%", marginBottom: 8 }}
            />
            <button className="btn-primary btn-block" onClick={verifyCode} disabled={busy || !code.trim()}>
              {busy ? "Checking…" : "Sign in"}
            </button>
            <button className="link" style={{ marginTop: 8 }} onClick={() => { setCodeSent(false); setCode(""); }}>← different email</button>
          </>
        )}
        {err && <p style={{ color: "#f87171", fontSize: 12, marginTop: 8 }}>{err}</p>}
      </div>
    </div>
  );
}

// AI-data consent screen (Guideline 5.1.1(i)/5.1.2). Names the data sent + the providers + the
// purpose, links the Privacy Policy, requires "I Agree". Shown once, right after sign-in.
function AiConsent({ onAccept }: { onAccept: () => void }) {
  const openPrivacy = () => {
    const url = "https://orioralabs.com/about/privacy";
    if (isTauri) void invoke("open_external", { url });
    else window.open(url, "_blank");
  };
  return (
    <div className="center">
      <div className="login-card">
        <div className="brand-lg">Oriora <span className="dot">Content</span></div>
        <h1>How Oriora uses AI</h1>
        <p style={{ textAlign: "left" }}>
          When you use an AI feature, Oriora Content sends the data for that action — your message, your
          drafts, your voice profile, and content from the sources you follow — to third-party AI providers
          (via your own connected AI key) to draft and coach your posts.
        </p>
        <p style={{ textAlign: "left" }}>
          Your data stays yours — we don't retain it: data passes through Oriora for each AI action and
          isn't kept by us. It lives on this device and in your own GitHub. Nothing posts without your
          explicit approval. By continuing, you agree to this.
        </p>
        <button className="btn-primary btn-block" onClick={onAccept}>I Agree</button>
        <button className="btn btn-block" style={{ marginTop: 8 }} onClick={openPrivacy}>Privacy Policy</button>
      </div>
    </div>
  );
}

// The standard Oriora account section (the Expense popover — required on every app):
// Signed in as · Sign out · Account management → Delete account · wallet top-up (IAP on iOS,
// web link elsewhere) · "How to use →".
function AccountMenu({ email, session }: { email: string; session: Session }) {
  const [open, setOpen] = useState(false);
  const [showMgmt, setShowMgmt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const openExternal = (url: string) => {
    if (isTauri) void invoke("open_external", { url });
    else window.open(url, "_blank");
  };

  const doDelete = async () => {
    const at = session.access_token;
    const rt = session.refresh_token;
    if (isIOSNative) {
      await deleteAccountIos(at, rt);
    } else if (isTauri) {
      await invoke("open_delete_window", { accessToken: at, refreshToken: rt });
    } else {
      window.open("https://orioralabs.com/account/delete", "_blank");
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => { setOpen((o) => !o); setShowMgmt(false); setConfirmDelete(false); }}
        aria-label="Account"
        style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--accent, #8b5cf6)", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", marginLeft: 8 }}>
        {(email[0] ?? "?").toUpperCase()}
      </button>
      {open && (
        <>
          <div onClick={() => { setOpen(false); setShowMgmt(false); setConfirmDelete(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="card" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 50, width: 250, padding: "0.85rem", textAlign: "left", maxHeight: "75vh", overflowY: "auto" }}>
            <div className="muted" style={{ fontSize: 11, marginBottom: 2 }}>Signed in as</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
            <button className="btn btn-block" style={{ fontSize: 12, marginBottom: 6 }}
              onClick={async () => { try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ } }}>Sign out</button>
            {!showMgmt ? (
              <button onClick={() => setShowMgmt(true)}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--ink-dim, #8a8a8a)" }}>Account management…</button>
            ) : !confirmDelete ? (
              <div style={{ marginTop: 6 }}>
                <button onClick={() => setConfirmDelete(true)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "#dc2626" }}>Delete account…</button>
                <span style={{ marginLeft: 10, fontSize: 11, color: "var(--ink-dim, #8a8a8a)", cursor: "pointer" }} onClick={() => setShowMgmt(false)}>← back</span>
              </div>
            ) : (
              <div style={{ marginTop: 6 }}>
                <div className="muted" style={{ fontSize: 11, marginBottom: 8, lineHeight: 1.45 }}>This permanently deletes your Oriora account. It can&apos;t be undone.</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ flex: 1, fontSize: 11, fontWeight: 600, background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, padding: "0.45rem", cursor: "pointer" }}
                    onClick={() => void doDelete()}>Delete</button>
                  <button className="btn" style={{ flex: 1, fontSize: 11 }} onClick={() => { setConfirmDelete(false); setShowMgmt(false); }}>Cancel</button>
                </div>
              </div>
            )}
            {isIOSNative || isMasMac ? (
              <WalletTopUp />
            ) : (
              <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border, #333)" }}>
                <button onClick={() => openExternal(ACCOUNT_URL)}
                  className="btn btn-block" style={{ fontSize: 12, fontWeight: 600 }}>
                  Manage your Oriora account →
                </button>
                <div className="muted" style={{ fontSize: 10, marginTop: 6, lineHeight: 1.4 }}>
                  AI runs through your Oriora account. Add funds in your Oriora account on the web.
                </div>
              </div>
            )}
            <div style={{ marginTop: "0.85rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border, #333)" }}>
              <button onClick={() => openExternal(HOW_TO_URL)}
                className="btn btn-block" style={{ fontSize: 12, fontWeight: 600 }}>
                How to use Oriora Content →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const PILL: Record<string, string> = { draft: "#6b7280", posted: "#8b5cf6", failed: "#dc2626" };

function Posts({ refreshKey }: { refreshKey: number }) {
  const [drafts, setDrafts] = useState<LocalDraft[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const f = () => setDrafts(listDrafts());
    f();
    const id = setInterval(f, 3000);
    return () => clearInterval(id);
  }, [refreshKey]);

  function doExport() {
    const blob = new Blob([exportData()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "oriora-content-data.json";
    a.click();
    URL.revokeObjectURL(url);
  }
  async function doImport(file: File) {
    try {
      const n = importData(await file.text());
      setDrafts(listDrafts());
      alert(`Imported ${n.drafts} drafts + ${n.chat} chat messages.`);
    } catch (e) {
      alert(`Import failed: ${String(e)}`);
    }
  }

  return (
    <div className="wrap">
      <div className="toolbar">
        <button className="btn btn-sm" onClick={doExport}>Export my data</button>
        <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>Import</button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) doImport(f); e.target.value = ""; }}
        />
      </div>
      <p className="note">
        Your chats and quick drafts live on this device; your review queue and preferences live in your
        own GitHub. AI features send only what each request needs, and Oriora doesn&apos;t retain it.
        Export anytime to keep a local copy.
      </p>

      {drafts.length === 0 && <p className="muted">Nothing yet — draft something in Chat.</p>}
      {drafts.map((d) => (
        <div key={d.id} className="card">
          <span className="pill" style={{ background: PILL[d.status] }}>{d.status}</span>
          <small className="muted" style={{ marginLeft: 8 }}>{d.platform}</small>
          <p>{d.content}</p>
          <div className="toolbar" style={{ marginBottom: 0 }}>
            <button className="btn btn-sm" onClick={() => { navigator.clipboard.writeText(d.content); }}>Copy post</button>
          </div>
          {d.post_url && <a href={d.post_url} target="_blank" rel="noreferrer">{d.post_url}</a>}
          {d.error && <p style={{ color: "#f87171", fontSize: 12 }}>{d.error}</p>}
        </div>
      ))}
    </div>
  );
}
